import React from 'react'
import { DropdownMenu } from "@pokemon/design.ui.input.dropdown-menu"
import shallowCompare from 'react-addons-shallow-compare'
import './date-picker.css'

/**
 * Creates an array of length `length`, with optional `mapfn` executed on each item.
 * @param length Desired length of the array.
 * @param mapfn Map function to use when creating array, executes it on each item before adding it to the array.
 */
function getArray(length: number, mapfn: (val: number, key: number) => number) {
  return Array.from<number, number>({ length }, mapfn)
}

/**
 * Props to use when instantiating a new `DatePicker` component.
 */
export interface DatePickerProps {
  /** 
   * The current date selection.
   */
  value: DatePickerSelection

  /**
   * Translated strings to use for month options.
   */
  months: string[]

  /**
   * Translated string to use for year dropdown label.
   */
  yearLabel: string

  /**
   * Translated string to use for month dropdown label.
   */
  monthLabel: string

  /**
   * Translated string to use for dy dropdown label.
   */
  dayLabel: string

  /** 
   * Handler to execute when the value changes.
   */
  onChange: (change: DatePickerSelection) => void

  /**
   * Handler to execute when a user focuses on the component.
   */
  onFocus?: React.FocusEventHandler<HTMLSelectElement>

  /**
   * Handler to execute when a user loses focus (blurs) of the element.
   */
  onBlur?: React.FocusEventHandler<HTMLSelectElement>
}

/**
 * Internal state of `DatePicker` component.
 */
interface DatePickerState {
  /**
   * The items that have been focused so far.
   */
  focusHistory: FocusBlurHistory

  /**
   * The items that have been blurred so far.
   */
  blurHistory: FocusBlurHistory
}

/**
 * Contains the full date selected by the trainer, as strings.
 */
export interface DatePickerSelection {
  /** 
   * The month part of a date, as a string numeral, ie. '2'.
   */
  month?: string,

  /** 
   * The day part of a date, as a string.
   */
  day?: string,

  /** 
   * The year part of a date, as string.
   */
  year?: string
}

/** 
 * Tracks whether items get focused or blurred.
 */
interface FocusBlurHistory {
  /**
   * Whether the year has been focused/blurred yet.
   */
  year: boolean

  /**
   * Whether the month has been focused/blurred yet.
   */
  month: boolean

  /**
   * Whether the day has been focused/blurred yet.
   */
  day: boolean
}

/**
 * A date picker allowing trainers to choose a day, month and year.
 */
export class DatePicker extends React.Component<DatePickerProps, DatePickerState> {
  /** 
   * Default prop values.
   */
  static defaultProps = {
    onFocus: () => { },
    onBlur: () => { }
  }

  constructor(props: DatePickerProps) {
    super(props)

    this.state = {
      focusHistory: {
        year: false,
        month: false,
        day: false
      },
      blurHistory: {
        year: false,
        month: false,
        day: false
      }
    }
  }

  /**
   * Determine if the component has changed and should update.
   */
  shouldComponentUpdate(nextProps: DatePickerProps, nextState: DatePickerState) {
    const updated = this.props.value!.year !== nextProps.value!.year ||
      this.props.value!.month !== nextProps.value!.month ||
      this.props.value!.day !== nextProps.value!.day ||
      shallowCompare(this, { ...nextProps, value: this.props.value }, nextState)

    return updated
  }

  /**
   * Get the max day based on month and year.
   */
  getMaxDay = (value: DatePickerSelection) => {
    return new Date(parseInt(value.year || '2000', 10), parseInt(value.month || '1', 10), 0).getDate()
  }

  resetDayIfInvalid = (value: DatePickerSelection) => {
    if (value.day && parseInt(value.day, 10) > this.getMaxDay(value)) {
      delete value.day
    }
  }

  /** 
   * Handle month dropdown changing.
   */
  handleChangeMonth = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const month = (this.props.months.indexOf(event.target.value) + 1).toString()
    const newValue = { ...this.props.value, month }
    this.resetDayIfInvalid(newValue)
    this.props.onChange(newValue)
  }

  /** 
   * Handle day dropdown changing.
   */
  handleChangeDay = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const day = event.target.value.toString()
    this.props.onChange({ ...this.props.value, day })
  }

  /** 
   * Handle year dropdown changing.
   */
  handleChangeYear = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const year = event.target.value.toString()
    const newValue = { ...this.props.value, year }
    if (newValue.month === '2') {
      this.resetDayIfInvalid(newValue)
    }
    this.props.onChange(newValue)
  }

  /**
   * Handle dropdown focus changing.
   */
  handleFocus = (fieldName: string) => {
    return (event: React.FocusEvent<HTMLSelectElement>) => {
      this.setState({
        focusHistory: {
          ...this.state.focusHistory,
          [fieldName]: true
        }
      }, () => {
        if (this.state.focusHistory.year && this.state.focusHistory.month && this.state.focusHistory.day) {
          this.props.onFocus!(event)
        }
      })
    }
  }

  /**
   * Handle dropdown blur changing.
   */
  handleBlur = (fieldName: string) => {
    return (event: React.FocusEvent<HTMLSelectElement>) => {
      this.setState({
        blurHistory: {
          ...this.state.blurHistory,
          [fieldName]: true
        }
      }, () => {
        if (this.state.blurHistory.year && this.state.blurHistory.month && this.state.blurHistory.day) {
          this.props.onBlur!(event)
        }
      })
    }
  }

  render() {
    const days = getArray(this.getMaxDay(this.props.value), (_, i) => i + 1)
    const years = getArray(120, (_, i) => new Date().getFullYear() - i)

    let month = this.props.value.month
    if (month) {
      month = this.props.months[parseInt(month, 10) - 1]
    }

    return (
      <div className="date-input-wrapper">
        <div className="date-select">
          <label className="date-label" htmlFor="year-select">{this.props.yearLabel}</label>
          <DropdownMenu className="date-input"
            name="year"
            onChange={this.handleChangeYear}
            onFocus={this.handleFocus('year')}
            onBlur={this.handleBlur('year')}
            options={years}
            value={this.props.value.year}
          />
        </div>
        <div className="date-select">
          <label className="date-label" htmlFor="month-select">{this.props.monthLabel}</label>
          <DropdownMenu className="date-input"
            name="month"
            onChange={this.handleChangeMonth}
            onFocus={this.handleFocus('month')}
            onBlur={this.handleBlur('month')}
            options={this.props.months}
            value={month}
          />
        </div>
        <div className="date-select">
          <label className="date-label" htmlFor="day-select">{this.props.dayLabel}</label>
          <DropdownMenu className="date-input"
            name="day"
            onChange={this.handleChangeDay}
            onFocus={this.handleFocus('day')}
            onBlur={this.handleBlur('day')}
            options={days}
            value={this.props.value.day}
          />
        </div>
      </div>
    )
  }
}
